import React, { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useParams } from 'react-router-dom';

import editIcon from 'images/directory/edit_icon.svg';
import locationIcon from 'images/directory/location_icon_grey.svg';
import messageIcon from 'images/directory/message_icon.svg';
import instagramLogo from 'images/instagram_black.svg';
import linkLogo from 'images/link_black.svg';
import linkedinLogo from 'images/linkedin_black.svg';
import twitterLogo from 'images/twitter_black.svg';

import Button from '@components/Button';
import { PrimaryButton } from '@components/Button/variants';
import EnableProfileModal from '@components/EnableProfileModal';
import FollowButton from '@components/FollowButton';
import FollowStatusBanner from '@components/FollowStatusBanner';
import IconX from '@components/Images/Rebranding/IconX';
import MessageModal from '@components/MessageModal';
import MetaTags from '@components/MetaTags';
import Spinner from '@components/Spinner';
import { AppContextProvider } from '@contexts/AppContext';
import useSubscriberComments from '@hooks/useSubscriberComments';
import useSubscriberProfile from '@hooks/useSubscriberProfile';
import useUserForumPosts from '@hooks/useUserForumPosts';
import MuiTooltip from '@mui/material/Tooltip';
import {
  edit_profile_settings_path,
  subscriber_directories_path,
  subscriber_profile_path,
} from '@routes';
import { FollowType } from '@utils/backend-api/follow';
import { ForumPostsResult } from '@utils/backend-api/forumPosts';
import { SubscriberProfile, generateSubscriberKey } from '@utils/backend-api/subscriber_directory';
import { POST_TYPE_ARTICLES } from '@utils/constants';

import ProfileContent from './Content';

const OG_IMAGE_PATH = '/global/og_images/profile_social_cover.png';

const SubscriberProfileContent = ({
  username,
  isSEOBot,
  initialSubscriberData,
  initialForumPostsData,
  postType,
  page,
}: {
  username: string;
  isSEOBot?: boolean;
  initialSubscriberData?: SubscriberProfile;
  initialForumPostsData?: ForumPostsResult;
  postType: string;
  page: string;
}) => {
  const [messageUser, setMessageUser] = useState(null);
  const { profile, isLoadingSubscriberProfile } = useSubscriberProfile({
    username,
    articlePageId: postType === POST_TYPE_ARTICLES ? page || 1 : 1,
    initialData: initialSubscriberData,
  });

  const canViewFullProfile = isSEOBot || profile?.isCurrentUserSubscriber || profile?.isReporter;
  // If canViewFullProfile is false use id 0 to get an empty forumPosts array to hide it
  const { forumPosts, areForumPostsLoading: isLoadingForumPosts } = useUserForumPosts({
    userId: canViewFullProfile ? profile?.id : 0,
    order: 'most_recent',
    initialData: initialForumPostsData,
  });

  const {
    data: subscriberCommentsData,
    isFetching: isFetchingComments,
    fetchNextPage: fetchMoreComments,
    hasNextPage: hasMoreComments,
  } = useSubscriberComments({
    commentType: 'comments',
    username: username,
    enabled: profile?.currentUserLoggedIn,
  });
  const {
    data: subscriberCommentLikesData,
    isFetching: isFetchingCommentLikes,
    fetchNextPage: fetchMoreCommentLikes,
    hasNextPage: hasMoreCommentLikes,
  } = useSubscriberComments({
    commentType: 'comment_likes',
    username: username,
    enabled: profile?.currentUserLoggedIn,
  });

  const isMessageModalEnabled = profile?.canSendMessage || profile?.requiresMessageEnable;
  const hasFollowRelationships = profile?.totalFollowers > 0 || profile?.totalFollowing > 0;

  const metaTitle = (profile) => {
    return [profile.name, [profile.titleAtCompany, profile.companyName].filter(Boolean).join(' @ ')]
      .filter(Boolean)
      .join(' - ');
  };

  const metaDescription = (profile) => {
    return [profile.titleAtCompany, profile.companyName, profile.oneLineBio]
      .filter((str) => str !== null && str !== '' && str !== undefined)
      .join(' | ');
  };

  return (
    <>
      {isLoadingSubscriberProfile || isLoadingForumPosts ? (
        <Spinner />
      ) : (
        <>
          <MetaTags
            title={metaTitle(profile)}
            description={metaDescription(profile)}
            canonicalPath={subscriber_profile_path({ username })}
            ogTitle={profile.name}
            ogImagePath={OG_IMAGE_PATH}
            twitterTitle={profile.name}
            ogSiteName="The Information"
          />
          <Container id="Subscriber">
            {profile.showEnableProfileModal && (
              <EnableProfileModal
                isLoggedIn={profile?.currentUserLoggedIn}
                isSubscriber={profile?.isCurrentUserSubscriber}
              />
            )}
            <Row className="profile-start">
              <Col className="col-sm-12 col-md-8 w-full sm:w-auto">
                <div className="profile-image-wrapper">
                  <div className="profile-img">
                    {profile.picture && !profile.isOldReporter ? (
                      <img src={profile.picture} />
                    ) : (
                      <div className="avatar-placeholder">
                        <div className="avatar-name">{profile.nameInitial}</div>
                      </div>
                    )}
                  </div>
                  <div className="profile-header-detail">
                    <div className="profile-header">
                      <h1 className="name-container">{profile.name}</h1>
                      {profile.followStatus !== null &&
                        profile.currentUserId !== profile.id &&
                        profile.isActive &&
                        profile.isCurrentUserSubscriber &&
                        !profile.isOldReporter && (
                          <FollowButton
                            tooltipEnabled
                            followStatus={profile.followStatus}
                            followeeId={profile.identityId}
                            followType={FollowType.Identity}
                            username={username}
                            name={profile.name}
                          />
                        )}
                    </div>
                    {!profile.isOldReporter && (
                      <div className="profile-header-wrapper">
                        {profile.oneLineBio && (
                          <div className="one-line-bio-wrapper">
                            <div className="one-line-bio-container">{profile.oneLineBio}</div>
                          </div>
                        )}
                        {(profile.titleAtCompany || profile.companyName) && (
                          <div className="profile-subheader">
                            <div>{profile.titleAtCompany}</div>
                            {profile.titleAtCompany && profile.companyName && (
                              <div className="dot-middle">.</div>
                            )}
                            {profile.companyName && (
                              <a
                                href={subscriber_directories_path({
                                  ai_query: `works for "${profile.companyName}"`,
                                })}
                              >
                                {profile.companyName}
                              </a>
                            )}
                          </div>
                        )}
                        {profile.isActive && (
                          <div className="profile-subheader">
                            Subscriber since {profile.subscriberSince}
                          </div>
                        )}
                        {canViewFullProfile ? (
                          <div className="social-contact-container" id="profile-seo-container">
                            <div className="social-contact-wrapper">
                              {profile.twitter && (
                                <div className="social-container">
                                  <a
                                    href={`https://www.x.com/${profile.twitter}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <IconX className="h-20 w-20" />
                                  </a>
                                </div>
                              )}
                              {profile.linkedin && (
                                <div className="social-container">
                                  <Button
                                    as="externalLink"
                                    href={`https://www.linkedin.com/in/${profile.linkedin}`}
                                  >
                                    <img src={linkedinLogo} alt="LinkedIn logo" />
                                  </Button>
                                </div>
                              )}
                              {profile.instagram && (
                                <div className="social-container">
                                  <a
                                    href={`https://www.instagram.com/${profile.instagram}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <img src={instagramLogo} alt="Instagram logo" />
                                  </a>
                                </div>
                              )}
                              {profile.personalUrl && (
                                <div className="social-container">
                                  <a href={profile.personalUrl} target="_blank" rel="noreferrer">
                                    <img src={linkLogo} role="presentation" />
                                  </a>
                                </div>
                              )}
                            </div>
                            {profile.location && (
                              <a
                                className="location-wrapper"
                                href={subscriber_directories_path({ location: profile.location })}
                              >
                                <img src={locationIcon} role="presentation"></img>
                                <div>{profile.location}</div>
                              </a>
                            )}
                          </div>
                        ) : (
                          // Show social media icons without links for leads and logged out users
                          <div className="social-contact-container">
                            <div className="social-contact-wrapper">
                              <div className="social-container">
                                <img src={twitterLogo} alt="Twitter logo" />
                              </div>
                              <div className="social-container">
                                <img src={linkedinLogo} alt="LinkedIn logo" />
                              </div>
                              <div className="social-container">
                                <img src={instagramLogo} alt="Instagram logo" />
                              </div>
                              <div className="social-container">
                                <img src={linkLogo} role="presentation" />
                              </div>
                              <div className="social-container">
                                <img src={locationIcon} role="presentation" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              {profile.isActive && profile.isCurrentUserSubscriber && !profile.isOldReporter && (
                <Col className="col-sm-12 col-md-4 contact-wrapper">
                  <div className="contact-container">
                    <div
                      className={`profile-contact-wrapper ${
                        hasFollowRelationships ? 'with-follow-banner' : ''
                      }`}
                    >
                      {hasFollowRelationships && (
                        <FollowStatusBanner
                          username={username}
                          followers={profile.followers}
                          following={profile.following}
                          totalFollowers={profile.totalFollowers}
                          totalFollowing={profile.totalFollowing}
                        />
                      )}
                      {profile.isOwnProfile ? (
                        <PrimaryButton href={edit_profile_settings_path()} as="link">
                          <img src={editIcon} role="presentation" className="mr-8" />
                          Update my profile
                        </PrimaryButton>
                      ) : (
                        <>
                          <div className="message-btn-container">
                            {profile?.currentUserCanMessage && !profile?.subHasMessageEnabled ? (
                              <MuiTooltip
                                title="This person has messaging disabled"
                                enterTouchDelay={0}
                                arrow
                              >
                                <div>
                                  <PrimaryButton
                                    as="button"
                                    disabled={!isMessageModalEnabled}
                                    onClick={() => setMessageUser(profile)}
                                    data-test="message-button"
                                  >
                                    <img
                                      className="self-center pr-10"
                                      src={messageIcon}
                                      role="presentation"
                                    />
                                    <span>Send a message</span>
                                  </PrimaryButton>
                                </div>
                              </MuiTooltip>
                            ) : (
                              <PrimaryButton
                                as="button"
                                disabled={!isMessageModalEnabled}
                                onClick={() => setMessageUser(profile)}
                                data-test="message-button"
                              >
                                <img
                                  className="self-center pr-10"
                                  src={messageIcon}
                                  role="presentation"
                                />
                                <span>Send a message</span>
                              </PrimaryButton>
                            )}
                          </div>
                          {isMessageModalEnabled && messageUser && (
                            <MessageModal user={messageUser} onClose={() => setMessageUser(null)} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            <ProfileContent
              bio={profile.bio}
              canViewFullProfile={canViewFullProfile}
              commentsData={subscriberCommentsData}
              commentLikesData={subscriberCommentLikesData}
              contributorContent={profile.contributorContent}
              contributorContentPagination={profile.contributorContentPagination}
              favoriteBooks={profile.favoriteBooks}
              favoriteShows={profile.favoriteShows}
              favoriteSportsToPlay={profile.favoriteSportsToPlay}
              favoriteSportsToWatch={profile.favoriteSportsToWatch}
              favoriteVacationSpots={profile.favoriteVacationSpots}
              fetchMoreCommentLikes={fetchMoreCommentLikes}
              fetchMoreComments={fetchMoreComments}
              findingNewJob={profile.findingNewJob}
              findingNewJobDesc={profile.findingNewJobDesc}
              forumPosts={forumPosts}
              forumPostPagination={initialForumPostsData}
              hasMoreComments={hasMoreComments}
              hasMoreCommentLikes={hasMoreCommentLikes}
              hiring={profile.hiring}
              hiringDesc={profile.hiringDesc}
              isCurrentUserLead={profile.isCurrentUserLead}
              isFetchingMoreCommentLikes={isFetchingCommentLikes}
              isFetchingMoreComments={isFetchingComments}
              isOwnProfile={profile.isOwnProfile}
              investingInStartups={profile.investingInStartups}
              investingInStartupsDesc={profile.investingInStartupsDesc}
              name={profile.name}
              isOldReporter={profile.isOldReporter}
              providingServices={profile.providingServices}
              providingServicesDesc={profile.providingServicesDesc}
              skills={profile.skills}
              topics={profile.topics}
              username={username}
            />
          </Container>
        </>
      )}
    </>
  );
};

const SubscriberProfileSPA = () => {
  const { username, postType, page } = useParams();

  return <SubscriberProfileContent username={username} page={page} postType={postType} />;
};

const queryClient = new QueryClient();

const SubscriberProfileServer = ({
  page,
  postType,
  env,
  initialSubscriberData,
  initialForumPostsData,
  isSEOBot,
  username,
}) => {
  const parsedInitialData = JSON.parse(initialSubscriberData);
  const parsedForumPostsData = JSON.parse(initialForumPostsData);

  queryClient.setQueryData(generateSubscriberKey(username), parsedInitialData);

  return (
    <AppContextProvider env={env}>
      <QueryClientProvider client={queryClient}>
        <SubscriberProfileContent
          username={username}
          isSEOBot={isSEOBot}
          initialSubscriberData={parsedInitialData}
          initialForumPostsData={parsedForumPostsData}
          postType={postType}
          page={page}
        />
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export { SubscriberProfileSPA, SubscriberProfileServer };
